/**
 * Точка водоснабжения на чертеже: стояк, коллектор, водорозетка — на плане и на развёртке.
 *
 * В отличие от приборов, у точки нет белой подложки и укрупнения: водорозетка стоит за
 * умывальником или под полотенцесушителем, и подложка накрывала бы сам прибор. Рисуется
 * только символ в цвет воды, поэтому при отдалении точки тают вместе с планом, а не
 * наезжают друг на друга. Рисуется в локальных координатах — начало в центре объекта,
 * ось X вдоль стены. Начинка — символ раздела, здесь выделение, «без трубы» и зона захвата.
 */
import type { PointerEvent } from "react";
import type { Fixture } from "@/shared/plan";
import type { CatalogItem } from "@/shared/catalog";
import type { SymbolFacing } from "@/shared/ui";
import { CatalogSymbol, symbolRenderSize } from "../../CatalogSymbol";

/** Цвет и габариты, если типа в каталоге больше нет — объект всё равно виден и удаляем. */
const MISSING = { width: 60, height: 60, depth: 40, color: "#9aa3b2" };

const SELECTED_COLOR = "#2f6feb";
const UNCONNECTED_COLOR = "#d97706";

interface Props {
	fixture: Fixture;
	/** Тип из каталога; null — тип удалён из каталога */
	item: CatalogItem | null;
	selected: boolean;
	/** Точка без трубы — обводим предупреждающим пунктиром */
	unconnected?: boolean;
	/** Размер миллиметра в пикселях — линии постоянной толщины на экране */
	mmPerPx: number;
	interactive?: boolean;
	/** Минимальный размер символа на экране, пикселей */
	minPx?: number;
	/** Сверху на плане или спереди на развёртке */
	facing?: SymbolFacing;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const WaterPointShape = ({
	fixture,
	item,
	selected,
	unconnected = false,
	mmPerPx,
	interactive = true,
	minPx = 0,
	facing = "plan",
	onPointerDown,
}: Props) => {
	const size = item ?? MISSING;
	// На плане точка видна сверху и занимает от стены свою глубину, на развёртке — высоту
	const second = facing === "plan" ? size.depth : size.height;
	const { width: w, height: h } = symbolRenderSize(size.width, second, mmPerPx, 1, minPx);
	const px = (n: number) => n * mmPerPx;
	const color = selected ? SELECTED_COLOR : size.color;
	const r = Math.max(w, h) / 2;

	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "grab" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			{selected && (
				<circle
					cx={0}
					cy={0}
					r={r + px(3)}
					fill={`${SELECTED_COLOR}22`}
					stroke={SELECTED_COLOR}
					strokeWidth={px(1)}
				/>
			)}
			<CatalogSymbol
				item={item}
				width={w}
				height={h}
				color={color}
				mmPerPx={mmPerPx}
				facing={facing}
			/>
			{/* Точка без трубы: пунктирное кольцо, чтобы на плане было видно, куда ещё не подвели воду */}
			{unconnected && !selected && (
				<circle
					cx={0}
					cy={0}
					r={r + px(2.5)}
					fill="none"
					stroke={UNCONNECTED_COLOR}
					strokeWidth={px(1)}
					strokeDasharray={`${px(2.5)} ${px(2)}`}
				/>
			)}
			{/* Прозрачная зона захвата: сам значок мелкий, попасть по нему трудно */}
			<rect
				data-role="fixture"
				data-id={fixture.id}
				x={-w / 2 - px(5)}
				y={-h / 2 - px(5)}
				width={w + px(10)}
				height={h + px(10)}
				fill="transparent"
			/>
		</g>
	);
};
